/**
 * ActiveIndicatorsBar
 *
 * Renders active (applied) indicators as a compact stacked list
 * anchored to the bottom-left corner of the chart container.
 * Each row shows: eye toggle · name · settings gear (with popup) · remove.
 */

import React, { useState, useRef, useEffect, useLayoutEffect } from "react";
import { indicatorEngine } from "../../indicators/engine/indicator-engine";
import { ActiveIndicator, IndicatorParam } from "../../indicators/types/indicator.types";
import "./ActiveIndicatorsBar.scss";

interface ActiveIndicatorsBarProps {
  activeIndicators: ActiveIndicator[];
  onRemove: (instanceId: string) => void;
  onToggleVisibility: (instanceId: string) => void;
  onParamsChange: (instanceId: string, params: Record<string, any>) => void;
  colorTheme: string;
}

const getIndicatorLabel = (instance: ActiveIndicator): string => {
  const def = indicatorEngine.get(instance.indicatorId);
  if (!def) return instance.indicatorId;
  const paramValues = def.params
    .filter((p: IndicatorParam) => p.type === "number" && !p.hidden)
    .slice(0, 3)
    .map((p: IndicatorParam) => instance.params[p.key] ?? p.default);
  return paramValues.length > 0
    ? `${def.name} (${paramValues.join(", ")})`
    : def.name;
};

const EyeOpenIcon = () => (
  <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="1.8">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

const EyeClosedIcon = () => (
  <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="1.8">
    <line x1="1" y1="1" x2="23" y2="23" strokeLinecap="round" />
    <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
    <path d="M6.53 6.53A18.2 18.2 0 0 0 1 12s4 8 11 8a9.12 9.12 0 0 0 5.24-1.66" />
  </svg>
);

const GearIcon = () => (
  <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="1.8">
    <circle cx="12" cy="12" r="3" />
    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
  </svg>
);

const CloseIcon = () => (
  <svg viewBox="0 0 24 24" width="11" height="11" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const ActiveIndicatorsBar: React.FC<ActiveIndicatorsBarProps> = ({
  activeIndicators,
  onRemove,
  onToggleVisibility,
  onParamsChange,
  colorTheme,
}) => {
  const [openSettings, setOpenSettings] = useState<string | null>(null);
  const barRef = useRef<HTMLDivElement>(null);
  const settingsWrapperRef = useRef<HTMLDivElement | null>(null);
  const [popupMaxHeight, setPopupMaxHeight] = useState<number>(520);

  useEffect(() => {
    if (!openSettings) return;
    const handleOutside = (e: MouseEvent) => {
      if (barRef.current && !barRef.current.contains(e.target as Node)) {
        setOpenSettings(null);
      }
    };
    document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, [openSettings]);

  useLayoutEffect(() => {
    if (!openSettings) return;
    const measure = () => {
      const el = settingsWrapperRef.current;
      if (!el) return;
      const gear = el.getBoundingClientRect();
      let clipTop = 90;
      let node = el.parentElement;
      while (node && node !== document.body && node !== document.documentElement) {
        const s = getComputedStyle(node);
        if (s.overflowY !== "visible" || s.overflowX !== "visible") {
          clipTop = node.getBoundingClientRect().top;
          break;
        }
        node = node.parentElement;
      }
      setPopupMaxHeight(Math.max(160, Math.floor(gear.top - clipTop - 8)));
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [openSettings]);

  if (activeIndicators.length === 0) return null;

  const renderParamEditor = (instance: ActiveIndicator) => {
    const def = indicatorEngine.get(instance.indicatorId);
    const visibleParams = def ? def.params.filter((p: IndicatorParam) => !p.hidden) : [];
    if (!def || visibleParams.length === 0) {
      return <div className="aib-no-params">No configurable settings</div>;
    }
    return (
      <div className="aib-params">
        {visibleParams.map((p: IndicatorParam, idx: number) => {
          const prev = idx > 0 ? visibleParams[idx - 1] : null;
          const showGroup = Boolean(p.group) && (!prev || prev.group !== p.group);
          return (
            <React.Fragment key={p.key}>
              {showGroup && <div className="aib-param-group">{p.group}</div>}
              <div className="aib-param-row">
                <label title={p.label}>{p.label}</label>
            {p.type === "number" && (
              <input
                type="number"
                title={p.label}
                value={instance.params[p.key] ?? p.default}
                min={p.min}
                max={p.max}
                step={p.step || 1}
                onChange={(e) => {
                  const raw = e.target.value;
                  if (raw === "") return;
                  const n = Number(raw);
                  if (!Number.isFinite(n)) return;
                  onParamsChange(instance.instanceId, {
                    ...instance.params,
                    [p.key]: n,
                  });
                }}
              />
            )}
            {p.type === "color" && (
              <input
                type="color"
                title={p.label}
                value={instance.params[p.key] ?? p.default}
                onChange={(e) =>
                  onParamsChange(instance.instanceId, {
                    ...instance.params,
                    [p.key]: e.target.value,
                  })
                }
              />
            )}
            {p.type === "select" && p.options && (
              <select
                title={p.label}
                value={instance.params[p.key] ?? p.default}
                onChange={(e) =>
                  onParamsChange(instance.instanceId, {
                    ...instance.params,
                    [p.key]: e.target.value,
                  })
                }
              >
                {p.options.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            )}
            {p.type === "text" && (
              <input
                type="text"
                title={p.label}
                placeholder={p.placeholder}
                value={instance.params[p.key] ?? p.default}
                onChange={(e) =>
                  onParamsChange(instance.instanceId, {
                    ...instance.params,
                    [p.key]: e.target.value,
                  })
                }
              />
            )}
            {p.type === "boolean" && (
              <button
                type="button"
                title={p.label}
                role="switch"
                aria-checked={Boolean(instance.params[p.key] ?? p.default)}
                className={`aib-switch ${Boolean(instance.params[p.key] ?? p.default) ? "aib-switch-on" : ""}`}
                onClick={() =>
                  onParamsChange(instance.instanceId, {
                    ...instance.params,
                    [p.key]: !Boolean(instance.params[p.key] ?? p.default),
                  })
                }
              >
                <span className="aib-switch-thumb" />
              </button>
            )}
              </div>
            </React.Fragment>
          );
        })}
      </div>
    );
  };

  return (
    <div className={`active-indicators-bar ${colorTheme}`} ref={barRef}>
      {activeIndicators.map((inst) => (
        <div
          key={inst.instanceId}
          className={`aib-item${!inst.visible ? " aib-hidden" : ""}`}
        >
          {/* Eye toggle */}
          <button
            className="aib-btn aib-eye"
            onClick={() => onToggleVisibility(inst.instanceId)}
            title={inst.visible ? "Hide indicator" : "Show indicator"}
          >
            {inst.visible ? <EyeOpenIcon /> : <EyeClosedIcon />}
          </button>

          {/* Indicator name */}
          <span className="aib-name" title={getIndicatorLabel(inst)}>
            {getIndicatorLabel(inst)}
          </span>

          {/* Right-aligned actions */}
          <div className="aib-actions">
            {/* Settings */}
            <div
              className="aib-settings-wrapper"
              ref={openSettings === inst.instanceId ? settingsWrapperRef : undefined}
            >
              <button
                className={`aib-btn aib-gear${openSettings === inst.instanceId ? " active" : ""}`}
                onClick={() =>
                  setOpenSettings(
                    openSettings === inst.instanceId ? null : inst.instanceId
                  )
                }
                title="Settings"
              >
                <GearIcon />
              </button>
              {openSettings === inst.instanceId && (
                <div
                  className="aib-settings-popup"
                  style={{ maxHeight: popupMaxHeight }}
                >
                  <div className="aib-settings-header">
                    <span className="aib-settings-title">
                      {indicatorEngine.get(inst.indicatorId)?.name || inst.indicatorId}
                    </span>
                    <button
                      className="aib-btn aib-popup-close"
                      onClick={(e) => { e.stopPropagation(); setOpenSettings(null); }}
                      title="Close"
                    >
                      <CloseIcon />
                    </button>
                  </div>
                  {renderParamEditor(inst)}
                </div>
              )}
            </div>

            {/* Remove */}
            <button
              className="aib-btn aib-remove"
              onClick={() => {
                onRemove(inst.instanceId);
                if (openSettings === inst.instanceId) setOpenSettings(null);
              }}
              title="Remove indicator"
            >
              <CloseIcon />
            </button>
          </div>
        </div>
      ))}
    </div>
  );
};

export default ActiveIndicatorsBar;
